<template>
    <div>
        <List
            :title="$t('activities.activities')"
            :data="eventsStore.events"
            :dataFilter="eventsListConfig"
            :showDateRange="true"
            :dateColumn="'date'"
            :newButtonLabel="$t('activities.create_activity')"
            :actionButtons="{
                edit: true,
                delete: true,
                download: true
            }"
            :deleteDialogTitle="$t('events.delete_event')"
            :deleteDialogMessage="$t('events.are_you_sure')"
            @new="handleNew"
            @edit="handleEdit"
            @delete="handleDelete"
            @download="handleDownload"
            @row-click="handleRowClick"
        >
            <template #body-date="{ data }">
                {{ formatDate(data.date) }}
            </template>
            <template #actions="{ row }">
                <div class="flex gap-1 items-center">
                    <Button
                        icon="pi pi-pencil"
                        text
                        rounded
                        size="small"
                        v-tooltip="$t('events.edit') || 'Editar'"
                        @click.stop="handleEdit(row)"
                    />
                    <Button
                        icon="pi pi-download"
                        text
                        rounded
                        size="small"
                        :loading="downloadingId === row.id"
                        v-tooltip="$t('events.download') || 'Descargar'"
                        @click.stop="handleDownload(row)"
                    />
                    <Button
                        icon="pi pi-trash"
                        text
                        rounded
                        severity="danger"
                        size="small"
                        v-tooltip="$t('events.delete') || 'Eliminar'"
                        @click.stop="openDeleteDialog(row)"
                    />
                    <Button
                        icon="pi pi-sign-in"
                        text
                        rounded
                        size="small"
                        severity="success"
                        v-tooltip="$t('events.enter') || 'Entrar'"
                        @click.stop="handleEnterDashboard(row)"
                    />
                </div>
            </template>
        </List>
        
        <EventContentDownloadDialog />

        <!-- Delete Confirmation Dialog -->
        <Dialog
            :visible="showDeleteDialog"
            :header="deleteDialogTitle"
            :modal="true"
            :style="{ width: '450px' }"
            @update:visible="showDeleteDialog = $event"
        >
            <p>{{ deleteDialogMessage }}: {{ selectedRow && selectedRow.name }}</p>
            <template #footer>
                <Button
                    :label="$t('events.cancel') || 'Cancelar'"
                    icon="pi pi-times"
                    text
                    @click="showDeleteDialog = false"
                />
                <Button
                    :label="$t('events.accept') || 'Eliminar'"
                    icon="pi pi-check"
                    severity="danger"
                    :loading="deleting"
                    @click="confirmDelete"
                />
            </template>
        </Dialog>
    </div>
</template>
<script lang="ts" setup>
import { ref, computed } from "vue"
import { useRouter } from "vue-router"
import { useEventsStore } from "@/stores/apps/events"
import { useStoreI18n } from "@/composables/useStoreI18n"
import List from "@/components/common/List.vue"
import EventContentDownloadDialog from "@/components/common/EventContentDownloadDialog.vue"
import Button from "primevue/button"
import Dialog from "primevue/dialog"
import { useToast } from "primevue/usetoast"
import { eventsList, isNoEventContentError, setDemoFilter } from "./eventsList"
import dayjs from "@/utils/dayjs"

const router = useRouter()
const eventsStore = useEventsStore()
const { t } = useStoreI18n()
const toast = useToast()

const props = defineProps({
    demo: {
        type: Boolean,
        default: false
    },
})

type Event = {
    id: number
    name: string
    api_key: string
    activity: {code: string, name: string, id: number}
    activity_name: string
    date: Date | string
    comments: string
    logo: string | null
    finished: boolean
    demo: boolean
}

const downloadingId = ref<number | null>(null)
const showDeleteDialog = ref(false)
const selectedRow = ref<Event | null>(null)
const deleting = ref(false)

const deleteDialogTitle = computed(() => t('events.delete_event') || 'Eliminar evento')
const deleteDialogMessage = computed(() => t('events.are_you_sure') || '¿Estás seguro?')

// Set demo filter in eventsList
setDemoFilter(props.demo)

// Create eventsList config with demo filter
const eventsListConfig = computed(() => {
    return {
        ...eventsList,
        filter_gateway: (filters: any) => {
            let result = eventsList.filter_gateway(filters)
            // Apply demo filter
            if (props.demo !== null) {
                result = result.filter((item: Event) => item.demo === props.demo)
            }
            return result
        }
    }
})

const formatDate = (date: Date | string) => {
    if (!date) return ''
    const d = dayjs(date)
    return d.isValid() ? d.format('DD/MM/YYYY') : ''
}

const handleNew = () => {
    router.push('/events')
}

const handleEdit = (row: Event) => {
    router.push("/events/" + row.api_key)
}

const openDeleteDialog = (row: Event) => {
    selectedRow.value = row
    showDeleteDialog.value = true
}

const confirmDelete = async () => {
    if (!selectedRow.value) return
    
    deleting.value = true
    try {
        await eventsList.deleteEvent(selectedRow.value.id)
        
        // Refresh data
        await eventsStore.getEvents()
        
        toast.add({
            severity: 'success',
            summary: t('messages.success') || 'Éxito',
            detail: t('messages.event_deleted') || 'Evento eliminado correctamente',
            life: 3000
        })
        
        showDeleteDialog.value = false
        selectedRow.value = null
    } catch (error) {
        console.error('Error deleting event:', error)
        toast.add({
            severity: 'error',
            summary: t('messages.error') || 'Error',
            detail: t('messages.error_deleting_event') || 'No se pudo eliminar el evento',
            life: 5000
        })
    } finally {
        deleting.value = false
    }
}

const handleDelete = (_row: Event) => {
    // This is called by the List component but we handle it in our custom actions
}

const handleDownload = async (row: Event) => {
    downloadingId.value = row.id
    try {
        await eventsList.downloadEvent(row.id)
        toast.add({
            severity: 'success',
            summary: t('messages.success') || 'Éxito',
            detail: t('messages.download_completed') || 'Descarga iniciada',
            life: 3000
        })
    } catch (error) {
        if (isNoEventContentError(error)) {
            toast.add({
                severity: 'warn',
                summary: t('events.no_content') || 'Sin contenido',
                detail: t('events.no_content_in_activity') || 'No hay contenido en esta actividad',
                life: 5000
            })
            return
        }

        console.error('Error downloading:', error)
        toast.add({
            severity: 'error',
            summary: t('messages.error') || 'Error',
            detail: t('messages.error_downloading') || 'No se pudo descargar el archivo',
            life: 5000
        })
    } finally {
        downloadingId.value = null
    }
}

const handleEnterDashboard = (row: Event) => {
    router.push("/events/dashboard/" + row.api_key)
}

const handleRowClick = (row: Event) => {
    router.push("/events/dashboard/" + row.api_key)
}
</script>

